import { Option } from "@interfaces/legal-processes/legal-process-document.interface";
import { TagVariant } from 'logical-growth-components';
import {
  StatusTag,
  TagVariantMap,
} from '@utils/interfaces/tag-colors.interface';

export const PASSWORD_PATTERN =
  '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)[A-Za-z\\d$@$!%*?&_]{7,10}';

export const WOMPI_CHECKOUT_STATUS = {
  DECLINED: 'Rechazado',
  APPROVED: 'Aprobado',
  ERROR: 'Error',
  VOIDED: 'Anulada',
};

export const buttonStylesArr = [
  'primary',
  'secondary',
  'outline',
  'destructive',
  'action-btn',
];

export type buttonStyles =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'destructive'
  | 'action-btn';

export type ComponentStatus = 'init' | 'loading' | 'error' | 'success';

export type ButtonTypes = 'button' | 'reset' | 'submit';

export type CheckoutStatus = 'APPROVED' | 'DECLINED' | 'ERROR' | 'VOIDED';

export type UserType = 'admin' | 'user';

export enum UserTypeEnum {
  Admin = 'admin',
  User = 'user',
}

export const btnStylesDicc: Record<string, string> = {
  primary: 'btn__primary',
  secondary: 'btn__secondary',
  outline: 'btn__outline',
  destructive: 'btn__destructive',
  'action-btn': 'btn__table-action',
};

export const maritalStatusDicc: Record<string, string> = {
  married: 'Casado',
  single: 'Soltero',
  divorced: 'Divorciado',
  widowed: 'Viudo',
  free_union: 'Unión libre',
};

export const educationLevelDicc: Record<string, string> = {
  bachelor: 'Bachiller',
  technical: 'Técnico',
  technologist: 'Tecnólogo',
  professional: 'Profesional',
};

export const documentTypesDicc: Record<string, string> = {
  cc: 'Cédula de ciudadanía',
  ce: 'Cédula de extranjería',
  pep: 'Permiso especial de permanencia',
  passport: 'Pasaporte',
};

export const EXTENDED_DOCUMENT_TYPES_DICC: Record<string, string> = {
  ...documentTypesDicc,
  NIT: 'NIT',
};

export const DOCUMENT_TYPE_ABBREVIATION: Record<string, string> = {
  cc: 'C.C ',
  ce: 'C.E ',
  pep: 'Permiso especial de permanencia',
  passport: 'Pasaporte',
};

export const documentTypeOption: Option[] = [
  {
    code:  'cc',
    label: 'Cédula de ciudadanía'
  },
  {
    code:  'ce',
    label: 'Cédula de extranjería'
  },
  {
    code:  'pep',
    label: 'Permiso especial de permanencia'
  },
  {
    code:  'passport',
    label: 'Pasaporte'
  },
  {
    code:  'nit',
    label: 'NIT'
  }
];

export const ocupationDicc: Record<string, string> = {
  employee: 'Empleado',
  independent: 'Independiente',
  retired: 'Jubilado',
};

export const modulesDicc = {
  modules: 'Módulos',
  users: 'Usuarios',
  roles: 'Roles',
  emails: 'Correos',
  alarms: 'Alertas',
  breach_reports: 'Reportes de incumplimientos',
  clients: 'Clientes',
  collection_process: 'Procesos de cobranza',
  collection_house: 'Casa de cobranza',
  damage_and_missing_reports: 'Reporte de daños y faltantes',
  debtors: 'Deudores',
  legal_process: 'Procesos legales',
  payment_agreement: 'Acuerdos de pago',
  payment_commitment: 'Compromisos de pago',
  tenants: 'Inquilinos',
  database_query: 'Consulta en base de datos'
};

export const permissionsDicc = {
  create: 'Crear',
  modify: 'Modificar',
  view: 'Ver',
  delete: 'Borrar',
};

export const communicationMediumDicc: Record<string, string> = {
  whatsapp: 'WhatsApp',
  mail: 'Correo físico',
  email: 'Correo electrónico',
  phone_call: 'Llamada telefónica',
  sms: 'Mensaje de texto',
  other: 'Otra',
};

export const ALARM_PRIORITY_DICC: Record<string, string> = {
  expired: 'Expirada',
  low: 'Prioridad baja',
  medium: 'Prioridad media',
  high: 'Prioridad alta',
};

export const ALARM_PRIORITY_TAG_VARIANTS: TagVariantMap = {
  high: 'error',
  medium: 'warning',
  low: 'success',
  expired: 'neutral',
};

export const ALARM_READ_STATUS_DICC: Record<string, string> = {
  true: 'Verdadero',
  false: 'Falso',
};

export const YEAR_MAPPING = {
  '=0': '0 años',
  '=1': '1 año',
  other: '# años',
};

export const MONTH_MAPPING = {
  '=0': '0 meses',
  '=1': '1 mes',
  other: '# meses',
};

export const DAYS_MAPPING = {
  '=0': '0 días',
  '=1': '1 día',
  other: '# días',
};
export type ProcessStatus =
  | 'pending'
  | 'normalized'
  | 'in_default_current'
  | 'in_default_restored';

export const PROCESS_STATUS_DICC: Record<string, string> = {
  pending: 'Pendiente',
  normalized: 'Normalizado',
  in_default_current: 'En mora vigente',
  in_default_restored: 'En mora restituido',
  no_contact: 'Sin contacto',
};

export const RESTRUCTURING_PROCESS_STATUS_DICC: Record<string, string> = {
  no_contact: 'Sin contacto',
  contacted: 'Contactado',
  in_negotiation: 'En negociaciones',
  payment_and_agreement_signed: 'Pago y firma de acuerdo',
  up_to_date_with_payments: 'Al día',
  behind_on_payments: 'Atraso en pagos',
  lawsuit_process: 'En proceso de demanda',
  closure: 'Cerrado',
  portfolio_penalty: 'Cartera castigada',
};

export type RestructuringProcessStatus =
  | 'no_contact'
  | 'contacted'
  | 'in_negotiation'
  | 'payment_and_agreement_signed'
  | 'up_to_date_with_payments'
  | 'behind_on_payments'
  | 'lawsuit_process'
  | 'closure'
  | 'portfolio_penalty';

export enum RestructuringProcessStatusEnum {
  NoContact = 'no_contact',
  Contacted = 'contacted',
  InNegotiation = 'in_negotiation',
  PaymentAndAgreementSigned = 'payment_and_agreement_signed',
  UpToDateWithPayments = 'up_to_date_with_payments',
  BehindOnPayments = 'behind_on_payments',
  LawsuitProcess = 'lawsuit_process',
  Closure = 'closure',
  PortfolioPenalty = 'portfolio_penalty',
}

export const PROCESS_GLOBAL_STATUS: Record<string, string> = {
  archived: 'Archivado',
  current: 'Vigente',
};

export const RESTRUCTURING_PROCESS_STATUS: Record<string, string> = {
  no_contact: 'Sin contacto',
  contacted: 'Contactado',
  in_negotiation: 'En negociaciones',
  payment_and_agreement_signed: 'Pago y firma de acuerdo',
  up_to_date_with_payments: 'Al día',
  behind_on_payments: 'Atraso en pagos',
  lawsuit_process: 'En proceso de demanda',
  closure: 'Cerrado',
  portfolio_penalty: 'Cartera castigada',
};

export const LEGAL_PROCESS_GLOBAL_STATUS: Record<string, string> = {
  current: 'Vigente',
  closed: 'Cerrado'
};

export const COVERAGE_STATUS_DICC: Record<string, string> = {
  finished: 'Finalizado',
  current: 'Vigente',
};

export const COVERAGE_STATUS_COLORS: Record<string, string> = {
  finished: 'red',
  current: 'green',
};

export const LEGAL_COVERAGE_REPORT_STATUS: Record<string, string> = {
  current: 'Vigente',
  closed: 'Cerrado'
};

export const LEGAL_COVERAGE_REPORT_STATUS_COLORS: Record<string, string> = {
  closed: 'red',
  current: 'green',
};

export const LEGAL_COVERAGE_STAGES: Record<string, string> = {
  collection_start: 'Inicio de cobro',
  conciliation: 'Conciliación',
  legal: 'Legal',
};

export const LEGAL_COVERAGE_SUBSTAGES: Record<string, Record<string, string>> = {
  collection_start: {},
  conciliation: {
    debtor_contact: 'Contacto directo con el deudor',
    negotiation: 'Propuesta de acuerdo de pago',
  },
  legal: {
    pre_executive_lawsuit: 'Demanda ejecutiva previa',
    hearing: 'Audiencia',
  },
};

export const COVERAGE_TYPE_DICC: Record<string, string> = {
  late_payment: 'Inmueble habitado con saldos en mora',
  restored: 'Inmueble restituido con saldos en mora',
  up_to_date: 'Inmueble al día',
};

export const PROCESS_STATUS_COLORS: Record<string, string> = {
  pending: 'gray',
  in_default_restored: 'yellow',
  in_default_current: 'orange',
  normalized: 'blue',
  no_contact: 'gray',
  contacted: 'blue',
  in_negotiation: 'orange',
  payment_and_agreement_signed: 'green',
  'payment_and_agreement signed': 'green',
  up_to_date_with_payments: 'green',
  behind_on_payments: 'orange',
  lawsuit_process: 'red',
  closure: 'gray',
  portfolio_penalty: 'red',
};

export const TAGS_TRANSLATIONS: Record<string, string> = {
  banned: 'Vetado',
  located: 'Localizado',
  notified: 'Notificado',
  unlocated: 'Ilocalizado',
  will_to_pay: 'Voluntad de pago',
  reported: 'Reportado',
};

export const TAGS_COLORS: Record<string, string> = {
  banned: 'red',
  reported: 'red',
  notified: 'yellow',
  unlocated: 'yellow',
  will_to_pay: 'blue',
  located: 'blue',
};

export const PAYMENT_CYCLES_DICC: Record<string, string> = {
  cycle_1: 'Ciclo 1',
  cycle_2: 'Ciclo 2',
};

export const PAYMENT_CYCLE_DAYS_DICC: Record<string, string> = {
  cycle_1: 'Día de pago 17',
  cycle_2: 'Día de pago 30',
};

export const CLIENT_PAYMENT_STATUS: Record<string, string> = {
  'up_to_date': 'Al día',
  'pending_payment': 'Pendiente de pago',
  'No aplica': 'No aplica',
};

export const CLIENT_PAYMENT_STATUS_COLORS: Record<string, string> = {
  'up_to_date': 'green',
  'pending_payment': 'secondary',
  'No aplica': 'gray',
};

export const PAYMENT_HISTORY_STATUS: Record<string, string> = {
  current: 'Vigente',
  closed: 'Cerrado',
  frozen: 'Congelado',
};

export const PAYMENT_HISTORY_STATUS_COLORS: Record<string, string> = {
  current: 'green',
  closed: 'gray',
  frozen: 'dark_blue',
};

export const ENTRENCHMENT_ORIGIN: Record<string, string> = {
  system: 'Sistema',
  transfer: 'Traslado',
};

export const REPORT_STATUS: Record<string, string> = {
  closed: 'Cerrado',
  current: 'Vigente',
  frozen: 'Congelado',
};

export const REPORT_STATUS_COLORS: Record<string, string> = {
  closed: 'gray',
  current: 'green',
  frozen: 'dark_blue',
};

export const TIMELINE_TYPES: Record<string, string> = {
  upload_file: 'Archivos subidos',
  assigned_to: 'Asignado a',
  collection_process_status_change: 'Cambio de estado de proceso de cobranza',
  collection_process_change_location: 'Cambio de ubicación a proceso de cobranza',
  collection_process_global_status_change: 'Cambio de estado global de proceso de cobranza',
  breach_report_creation: 'Creación de reporte de incumplimiento',
  breach_report_status_change: 'Cambio de estado de reporte de incumplimiento',
  comment: 'Comentario',
  payment_income: 'Ingreso de pago',
  payment_income_review: 'Revisión de ingreso de pago',
  payment_commitment: 'Compromiso de pago',
  payment_to_customer: 'Pago a cliente',
  process_creation: 'Creación de proceso de cobranza',
  send_notification: 'Envío de mensaje',
  report_central_risk: 'Reporte en centrales de riesgo',
  report_central_risk_notification:'Notificación de reporte en centrales de riesgo',
  public_service_report_creation: 'Creación de reporte de servicios públicos',
  damage_and_missing_report_creation: 'Creación de reporte de daños y faltantes',
  damage_and_missing_report_closed: 'Cierre de reporte de daños y faltantes',
  public_service_report_closed: 'Cierre de reporte de servicios públicos',
  email_notification: 'Notificación por correo electrónico',
  sms_notification: 'Notificación por mensaje de texto',
  payment_agreement_creation: 'Creación de acuerdo de pago',
  freeze_report: 'Reporte congelado',
  client_partial_payment: 'Abono a acreedor',
  fianly_partial_payment: 'Abono a Fianly',
  client_total_payment: 'Pago total acreedor',
  fianly_total_payment: 'Pago total Fianly',
  debtor_tag_creation: 'Etiquetado de deudor',
  legal_process_creation: 'Creación de proceso legal',
  legal_process_global_status_change: 'Cambio de estado global de proceso legal',
  process_status_change: 'Cambio de estado de proceso legal',
  process_type_change: 'Cambio el tipo del proceso legal',
  legal_process_comment: 'Comentario proceso legal',
  legal_process_payment_agreement: 'Acuerdo de pago de proceso legal',
  legal_process_user_in_charge_assignation: 'Usuario a cargo del proceso legal',
  global_status_change: 'Cambio de estado global de proceso legal',
  debtor_report_creation: 'Creación de reporte a deudor',
  deferrals_and_exclusions_creation: 'Creación de aplazamiento/exclusion',
  end_coverage: 'Finalización de cobertura',
  close_report: 'Cierre de reporte',
  assign_to: 'Asignado a gestor',
  create_comment: 'Comentario',
  create_report: 'Creación de reporte',
  create_legal_coverage: 'Creación de cobertura jurídica',
  notification: 'Notificación',
  change_stage: 'Cambio de etapa',
  update_for_free: 'Cobertura jurídica gratuita'
};

export const TIMELINE_TYPES_COLORS: Record<string, string> = {
  upload_file: '#E53935',
  assigned_to: '#D81B60',
  collection_process_status_change: '#8E24AA',
  breach_report_creation: '#1E88E5',
  breach_report_status_change: '#9E9E9E',
  comment: '#039BE5',
  send_notification: '#00ACC1',
  payment_income: '#43A047',
  payment_to_customer: '#FDD835',
  payment_commitment: '#558B2F',
  report_central_risk_notification: '#FFB300',
  report_central_risk: '#F4511E',
  payment_income_review: '#546E7A',
  process_creation: '#6D4C41',
  collection_process_change_location: '#C0CA33',
  collection_process_global_status_change: '#00ACC1',
  public_service_report_creation: '#00ACC1',
  damage_and_missing_report_creation: '#3D5AFE',
  damage_and_missing_report_closed: '#2979FF',
  public_service_report_closed: '#00838F',
  email_notification: '#990c11',
  sms_notification: '#126291',
  payment_agreement_creation: '#20dbbd',
  freeze_report: '#0D47A1',
  client_partial_payment: '#8BC34A',
  fianly_partial_payment: '#8BC34A',
  client_total_payment: '#8BC34A',
  fianly_total_payment: '#8BC34A',
  debtor_tag_creation: '#FD823F',
  legal_process_creation: '#00C853',
  legal_process_global_status_change: '#FF9800',
  process_status_change: '#9575CD',
  process_type_change: '#9575CD',
  legal_process_comment: '#FF7043',
  legal_process_payment_agreement: '#80D8FF',
  legal_process_user_in_charge_assignation: '#18FFFF',
  global_status_change: '#00ACC1',
  debtor_report_creation: '#E52000',
  legal_process_closed: '#45484A',
  deferrals_and_exclusions_creation: '#4800ff',
  end_coverage: '#F4511E',
  close_report: '#00838F',
  assign_to: '#8BC34A',
  create_comment: '#C0CA33',
  create_report: '#45484A',
  create_legal_coverage: '#126291',
  notification: '#00ACC1',
  change_stage: '#9C27B0',
  update_for_free: '#d440eeff'
};

export const TIMELINE_TYPES_ICONS: Record<string, string> = {
  upload_file: '<i class="fa-solid fa-file-arrow-up"></i>',
  assigned_to: '<i class="fa-solid fa-user"></i>',
  collection_process_status_change: '<i class="fa-solid fa-pen"></i>',
  breach_report_creation: '<i class="fa-solid fa-pen"></i>',
  breach_report_status_change: '<i class="fa-solid fa-repeat"></i>',
  comment: '<i class="fa-solid fa-comment"></i>',
  send_notification: '<i class="fa-solid fa-envelope-circle-check"></i>',
  payment_income: '<i class="fa-solid fa-piggy-bank"></i>',
  payment_to_customer: '<i class="fa-solid fa-hand-holding-dollar"></i>',
  report_central_risk_notification: '<i class="fa-solid fa-hotel"></i>',
  report_central_risk: '<i class="fa-solid fa-hotel"></i>',
  payment_income_review: '<i class="fa-solid fa-file-invoice"></i>',
  payment_commitment: '<i class="fa-solid fa-money-check-dollar"></i>',
  process_creation: '<i class="fa-solid fa-file-circle-check"></i>',
  collection_process_change_location: '<i class="fa-solid fa-repeat"></i>',
  collection_process_global_status_change: '<i class="fa-solid fa-repeat"></i>',
  public_service_report_creation: '<i class="fa-solid fa-file-circle-plus"></i>',
  damage_and_missing_report_creation: '<i class="fa-solid fa-file-circle-plus"></i>',
  damage_and_missing_report_closed: '<i class="fa-solid fa-file-circle-minus"></i>',
  public_service_report_closed: '<i class="fa-solid fa-file-circle-minus"></i>',
  email_notification: '<i class="fa-solid fa-envelope"></i>',
  sms_notification: '<i class="fa-solid fa-comment-sms"></i>',
  payment_agreement_creation: '<i class="fa-solid fa-money-check-dollar"></i>',
  freeze_report: '<i class="fa-solid fa-file-circle-exclamation"></i>',
  client_partial_payment: '<i class="fa-solid fa-file-invoice-dollar"></i>',
  fianly_partial_payment: '<i class="fa-solid fa-file-invoice-dollar"></i>',
  client_total_payment: '<i class="fa-solid fa-file-invoice-dollar"></i>',
  fianly_total_payment: '<i class="fa-solid fa-file-invoice-dollar"></i>"></i>',
  debtor_tag_creation: '<i class="fa-solid fa-tag"></i>',
  legal_process_creation: '<i class="fa-solid fa-gavel"></i>',
  legal_process_global_status_change: '<i class="fa-solid fa-repeat"></i>',
  process_status_change: '<i class="fa-solid fa-repeat"></i>',
  process_type_change: '<i class="fa-solid fa-repeat"></i>',
  legal_process_comment: '<i class="fa-solid fa-comment"></i>',
  legal_process_payment_agreement: '<i class="fa-solid fa-money-check-dollar"></i>',
  legal_process_user_in_charge_assignation: '<i class="fa-solid fa-user"></i>',
  global_status_change: '<i class="fa-solid fa-repeat"></i>',
  debtor_report_creation: '<i class="fa-solid fa-circle-xmark"></i>',
  legal_process_closed: '<i class="fa-solid fa-folder-closed"></i>',
  deferrals_and_exclusions_creation: '<i class="fa-solid fa-bullhorn"></i>',
  end_coverage: '<i class="fa-solid fa-calendar-xmark"></i>',
  close_report: '<i class="fa-solid fa-calendar-minus"></i>',
  assign_to: '<i class="fa-solid fa-user-plus"></i>',
  create_comment: '<i class="fa-solid fa-comment"></i>',
  create_report: '<i class="fa-solid fa-calendar-plus"></i>',
  create_legal_coverage: '<i class="fa-solid fa-calendar-check"></i>',
  notification: '<i class="fa-solid fa-envelope"></i>',
  change_stage: '<i class="fa-solid fa-list-check"></i>',
  update_for_free: '<i class="fa fa-legal"></i>'
};

export const MOVEMENTS_AMOUNT_TYPE: Record<string, string> = {
  client_partial_payment: 'Abono a acreedor',
  fianly_partial_payment: 'Abono a Fianly',
  client_total_payment: 'Pago total acreedor',
  fianly_total_payment: 'Pago total Fianly',
};

export const MOVEMENTS_AMOUNT_TYPE_CLIENT: Record<string, string> = {
  client_partial_payment: 'Abono a acreedor',
  client_total_payment: 'Pago total acreedor',
};

export const MOVEMENTS_AMOUNT_TYPE_FIANLY: Record<string, string> = {
  fianly_partial_payment: 'Abono a Fianly',
  fianly_total_payment: 'Pago total Fianly',
};

export const PUBLIC_SERVICES_TYPE: Record<string, string> = {
  energy: 'Energía',
  water: 'Agua',
  gas: 'Gas',
  other: 'Otro',
};

export const OPTIONS_COLLECTIONS: Record<string, string> = {
  'clausule_penal': 'Clausula penal',
  'public_services': 'Servicios públicos',
  'legal_expenses': 'Gastos jurídicos',
  'damages_and_missings': 'Daños y faltantes',
  'canon': 'Canon'
};

export const OPTIONS_ACTIONS: Record<string, string> = {
  report: 'Generar abono',
  collection: 'Generar cobros',
  projection: 'Generar proyección',
  administrative_management: 'Paso a gestión administrativa',
  send_payment_link: 'Generar enlace de pago',
};

export enum ACTIONS_TYPES {
  REPORT = 'report',
  COLLECTION = 'collection',
  PROJECTION = 'projection',
  SEND_PAYMENT_LINK = 'send_payment_link',
}

export const PAYMENT_LINK_STATUS: Record<string, string> = {
  approved: 'Aprobado',
  rejected: 'Rechazado',
  pending: 'Pendiente',
  failed: 'Fallido',
  reversed: 'Reversado',
  held: 'Retenido',
  started: 'Iniciado',
  expired: 'Expirado',
  abandoned: 'Abandonado',
  cancelled: 'Cancelado',
  anti_fraud: 'Antifraude',
  not_apply: 'No aplica',
};

export enum PaymentType {
  FIANLY_TOTAL = 'fianly_total_payment',
  FIANLY_PARTIAL = 'fianly_partial_payment',
  CLIENT_TOTAL = 'client_total_payment',
  CLIENT_PARTIAL = 'client_partial_payment',
}

export const ASPIRATIONS_MAP: Record<string, string> = {
  canon: 'Canon de arrendamiento',
  damage_and_missing: 'Daños y faltantes',
  legal_clause: 'Cláusula penal',
  management: 'Administración',
  other: 'Otro',
  public_services: 'Servicios públicos'
};

export const PROPERTY_TYPE: Record<string, string> = {
  Apartaestudio: 'apartaestudio.png',
  Apartamento: 'apartamento.png',
  Bodega: 'bodega.png',
  Casa: 'casa.png',
  'Casa Campestre': 'finca.png',
  Comercio: 'comercio.png',
  Consultorio: 'consultorio.png',
  Finca: 'finca.png',
  Habitación: 'habitacion.png',
  Local: 'local.png',
  Oficina: 'oficina.png',
  Vivienda: 'viviena.png',
};

export const PROPERTY_STATUS: Record<string, string> = {
  'Disponible': 'property__status-success',
  'En afianzamiento': 'property__status-warning',
  'Próximo': 'property__status-next',
  'En estudio': 'property__status-study',
};

export const NOTIFICATIONS_STATUS: Record<string, string> = {
  whatsapp: 'Whatsapp',
  email: 'Email',
  sms: 'SMS',
};

export type NotificationMedium = 'email' | 'sms';

export const NOTIFICATIONS_STATUS_COLORS: Record<string, string> = {
  whatsapp: 'green',
  email: 'blue',
  sms: 'red',
};

export const CHECKOUT_STATUS_COLORS = {
  DECLINED: '#ef5552',
  APPROVED: '#18BB45',
  ERROR: '#F59E0B',
  VOIDED: '#F59E0B',
};

export const CHECKOUT_STATUS_EPAYCO_COLORS: Record<string, string> = {
  rejected: '#ef5552',
  approved: '#18BB45',
  pending: '#F59E0B',
};

export const EPAYCO_CHECKOUT_STATUS: Record<string, string> = {
  rejected: 'Rechazado',
  approved: 'Aprobado',
  pending: 'Pendiente',
};

export const EPAYCO_PAYMENT_TYPE_TRANSLATION: Record<string, string> = {
  cash: 'Efectivo',
  TDC: 'Tarjeta de crédito',
  pse: 'PSE',
  daviplata: 'Daviplata',
  safetypay: 'SafetyPay',
};

export const MONTHS_CALENDAR: Record<string, string> = {
  1: 'Enero',
  2: 'Febrero',
  3: 'Marzo',
  4: 'Abril',
  5: 'Mayo',
  6: 'Junio',
  7: 'Julio',
  8: 'Agosto',
  9: 'Septiembre',
  10: 'Octubre',
  11: 'Noviembre',
  12: 'Diciembre',
};

export const PAYMENT_AGREEMENT_PAYMENT_TYPES = {
  initial_installment: 'Pago cuota inicial',
  installment_payment: 'Pago de cuota',
  extraordinary_capital_payment: 'Abono extra a capital',
  total_payment: 'Cancelación total del acuerdo',
  discounts: 'Descuentos',
  activation_payment: 'Pago de activación',
  initial_and_reactivation_payment: 'Pago inicial y activación',
};

export const BREACH_REPORT_PAYMENT_METHODS: Record<string, string> = {
  payment_link: 'Link de pago',
  bank_transfer: 'Transferencia bancaria',
  payment_transfer: 'Transferencia',
};

export const BANK_OPTIONS: Record<string, string> = {
  bancolombia: 'Bancolombia',
  davivienda: 'Davivienda',
};

export const ENTRENCHMENT_FILES_TRANSLATIONS: Record<string, string> = {
  special_arrangement: 'Acuerdo especial',
  contract: 'Contrato',
  payment_certificate: 'Pagaré',
  contract_modifications: 'Otrosí',
  document_postulant: 'Documento del arrendatario',
  document_co_signer: 'Documento del coarrendatario',
  generated_contract: 'Contrato generado',
  lease_contract: 'Contracto de arrendamiento',
  data_transfer: 'Certificado de transmisión de datos firmada',
  signed_services_contract: 'Contrato de prestación de servicios firmado',
  signed_power_of_attorney: 'Poder para iniciar procesos prejudiciales y judiciales',
  tenant_id: 'Documento de identidad arrendatario',
  cosigner_id: 'Documento de identidad coarrendatario',
  existence_and_legal_representation: 'Certificado de existencia y representación de la inmobiliaria'
};

export const LEGAL_TYPE: Record<string, string> = {
  chamber_of_comerce: 'Cámara de comercio',
  direct_conciliation: 'Conciliación directa',
  restitution_demand: 'Demanda de restitución',
  executive_demand: 'Demanda ejecutiva',
};

export const LEGAL_TYPE_COLORS: Record<string, string> = {
  chamber_of_comerce: 'yellow',
  direct_conciliation: 'green',
  restitution_demand: 'orange',
  executive_demand: 'blue',
};

export const DIRECT_CONCILIATION: Record<string, string> = {
  payment_agreement: 'Acuerdo de pago',
  breach_payment_agreement: 'Incumplimiento de acuerdo de pago',
  closed: 'Cerrado',
};

export const CHAMBER_OF_COMERCE: Record<string, string> = {
  initial_request: 'Solicitud inicial',
  pending_audience: 'Audiencia programada',
  celebrate_audience: 'Audiencia celebrada',
  signed_certificate: 'Certificado de audiencia firmado',
  constancy_attendance: 'Constancia de asistencia',
  constancy_desagree: 'Constancia de desacuerdo',
  constancy_non_attendance: 'Constancia de inasistencia',
  payment_agreement: 'Acuerdo de pago',
  breach_payment_agreement: 'Incumplimiento de acuerdo de pago',
  closed: 'Cerrado',
};

export const RESTITUTION_DEMAND: Record<string, string> = {
  initial_request: 'Solicitud inicial',
  in_distribution: 'Demanda en distribución',
  radicated_process: 'Demanda radicada',
  pending_audience: 'Audiencia programada',
  celebrate_audience: 'Audiencia celebrada',
  final_veredict: 'Veredicto final',
  closed: 'Cerrado',
};

export const EXECUTIVE_DEMAND: Record<string, string> = {
  initial_request: 'Solicitud inicial',
  in_distribution: 'Demanda en distribución',
  radicated_process: 'Demanda radicada',
  pending_audience: 'Audiencia programada',
  celebrate_audience: 'Audiencia celebrada',
  final_veredict: 'Veredicto final',
  closed: 'Cerrado',
};

export const LEGAL_STATUS: Record<string, Record<string, string>> = {
  chamber_of_comerce: CHAMBER_OF_COMERCE,
  direct_conciliation: DIRECT_CONCILIATION,
  restitution_demand: RESTITUTION_DEMAND,
  executive_demand: EXECUTIVE_DEMAND,
};

export const LEGAL_PROCESS_STATUS: Record<string, string> = {
  initial_request: 'Solicitud inicial',
  pending_audience: 'Audiencia programada',
  celebrate_audience: 'Audiencia celebrada',
  signed_certificate: 'Certificado de audiencia firmado',
  constancy_attendance: 'Constancia de asistencia',
  constancy_desagree: 'Constancia de desacuerdo',
  constancy_non_attendance: 'Constancia de inasistencia',
  payment_agreement: 'Acuerdo de pago',
  breach_payment_agreement: 'Incumplimiento de acuerdo de pago',
  final_veredict: 'Veredicto final',
  radicated_process: 'Demanda radicada',
  in_distribution: 'Demanda en distribución',
  closed: 'Cerrado',
};

export const LEGAL_PROCESS_STATUS_COLORS: Record<string, string> = {
  initial_request: '#039BE5',
  pending_audience: '#43A047',
  celebrate_audience: '#FDD835',
  signed_certificate: '#FFB300',
  constancy_attendance: '#546E7A',
  constancy_desagree: '#99A9C1',
  constancy_non_attendance: '#6D4C41',
  payment_agreement: '#3D5AFE',
  breach_payment_agreement: '#558B2F',
  final_veredict: '#FFB300',
  radicated_process: '#C0CA33',
  in_distribution: '#00ACC1',
  closed: '#607D8B',
};

export const LEGAL_DOCUMENTS_DICC: Record<string, string> = {
  payment_agreement: 'acuerdo de pago',
  payment_agreement_judge_peace: 'acuerdo de pago con juez de paz',
  assignment_executive_demand : 'cesión para demanda ejecutiva',
  reconciliation: 'conciliación',
  commissioning_office: 'despacho comisorio',
  executive_singular_demand: 'demanda singular',
  precautionary_measures: 'medidas cautelares',
  conciliation_power : 'poder conciliación',
  executive_power_demand : 'poder demanda ejecutiva',
  power_restore_leased_property:
    'poder de restitución de bien arrendado',
  real_estate_restitution_process: 'proceso de restitución de bien inmueble',
  executive_process: 'proceso ejecutivo',
};

export const LEGAL_DOCUMENTS_ROUTE_DICC: Record<string, string> = {
  commissioning_office: 'despacho-comisorio',
  real_estate_restitution_process: 'proceso-restitucion',
  executive_process: 'proceso-ejecutivo',
  executive_singular_demand: 'demanda-singular',
  precautionary_measures: 'medidas-cautelares',
  reconciliation: 'conciliacion',
  conciliation_power : 'poder-conciliacion',
  executive_power_demand : 'poder-demanda-ejecutiva',
  power_restore_leased_property:
    'poder-restitucion-bien-arrendado',
  assignment_executive_demand : 'cesion-demanda-ejecutiva',
  payment_agreement: 'acuerdo-pago',
  payment_agreement_judge_peace: 'acuerdo-pago-juez',
};

export const LEGAL_DOCUMENTS_ROUTE_REVERSE_DICC: Record<string, string> = {
  'despacho-comisorio': 'commissioning_office',
  'proceso-restitucion': 'real_estate_restitution_process',
  'proceso-ejecutivo': 'executive_process',
  'demanda-singular': 'executive_singular_demand',
  'medidas-cautelares': 'precautionary_measures',
  'conciliacion': 'reconciliation',
  'acuerdo-pago': 'payment_agreement',
  'acuerdo-pago-juez': 'payment_agreement_judge_peace',
  'poder-conciliacion' : 'conciliation_power',
  'poder-demanda-ejecutiva' : 'executive_power_demand',
  'poder-restitucion-bien-arrendado':
    'power_restore_leased_property',
  'cesion-demanda-ejecutiva' : 'assignment_executive_demand',
};

export const TYPE_PROCESS_DICC: Record<string, string> = {
  minimum: 'Mínima',
  minor: 'Menor',
  major: 'Mayor',
};

export const TYPE_UBICATION_DICC: Record<string, string> = {
  urban: 'Urbana',
  rural: 'Rural',
};

export const DEBTOR_TYPE_DICC: Record<string, string> = {
  tenant: 'Arrendatario',
  joint_tenant: 'Coarrendatario'
};

export const TRACKING_STATUS: Record<string, string> = {
  sent: 'Enviado',
  read: 'Leido',
  error: 'Error',
  default: ''
};

export const TRACKING_STATUS_COLORS: Record<string, string> = {
  sent: 'green',
  read: 'blue',
  error: 'red',
  default: 'gray'
};

export const TRACKING_STATUS_TAG_VARIANTS: Record<string, TagVariant> = {
  sent: 'success',
  read: 'info',
  error: 'error',
  default: 'neutral',
};

export const MAX_LAWYERS = 5;

export const INITIAL_YEAR = 2022;

export const TOOLTIP_NAME_INDICATOR : Record<string, string> = {
  'monthly_current_reports': 'Reportes',
  'reported_amounts_by_month': 'Monto',
  'paid_amount': 'Valor',
  'fianly_collection': 'Valor',
  'creditor_collection': 'Valor',
  'fianly_payments': 'Recaudo'
};

export const MONTHS: string[] = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

export const MONTHS_SHORT: string[] = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
];

export const TAG_COLORS: Record<string, string>  = {
  pending: '#999999',
  normalized: '#3c78d8',
  in_default_current: '#e69138',
  in_default_restored: '#f1c232'
};

export const PROCESS_STATUS_TAG_VARIANTS: Record<string, TagVariant> = {
  pending: 'neutral',
  normalized: 'info',
  in_default_current: 'brand',
  in_default_restored: 'warning',
  no_contact: 'neutral',
};

export const TYPES_DEFERRAL_EXCLUSION = {
  deferral: 'Aplazamiento',
  exclusion: 'Exclusión'
};

export const REASONS_DEFERRAL = {
  documents: 'Documentación',
  monthly_payment: 'Facturación mensual',
  refund_pending: 'Reintegro pendiente',
  supplanting: 'Suplantación'
};

export const REASONS_EXCLUSION = {
  ...REASONS_DEFERRAL,
  omission_of_requests: 'Omisión de solicitudes',
  payment_agreement: 'Acuerdo de pago',
  unoccupied_property: 'Inmueble no habitado',
};

export const PENDING_DOCUMENTS_DEFERRAL = {
  lease_contract: 'Contrato arrendamiento',
  tenant_id: 'Cédula arrendatario',
  co_tenant_id: 'Cédula coarrendatario',
  promissory_note: 'Pagaré',
  bank_account: 'Cuenta Bancaria',
};

export const PENDING_DOCUMENTS_EXCLUSION = {
  ...PENDING_DOCUMENTS_DEFERRAL,
  addendum: 'Otrosí',
  bank_certificate: 'Certificado bancario',
  transfer_contract: 'Cesión de contrato',
};

export const DEBTOR_ACTIONS = [
  {
    key: 'consulta',
    label: 'Realizar consulta',

  },
  {
    key: 'historial',
    label: 'Historial de consultas'
  }
];

export const PAYMENT_COMMITMENT_STATUS: Record<string, string> = {
  current: 'Vigente',
  finished: 'Finalizado'
};

export const PAYMENT_COMMITMENT_STATUS_COLORS: Record<string, string> = {
  current: 'green',
  finished: 'blue',
};

export const AUTHORIZATION_STATUS: Record<string, string> = {
  authorized: 'Autorizado',
  pending: 'Pendiente'
};

export const AUTHORIZATION_STATUS_TAG_VARIANTS: TagVariantMap = {
  [AUTHORIZATION_STATUS['authorized']]: 'success',
  [AUTHORIZATION_STATUS['pending']]: 'warning',
};

export const ACTIVE_STATUS_TAGS: Record<'true' | 'false', StatusTag> = {
  true: { label: 'Activo', variant: 'success' },
  false: { label: 'No activo', variant: 'error' },
};

export const MASS_COMMENTS_EXCEL_TEMPLATE = 'https://d2kh1tg8w32wdu.cloudfront.net/comments_format.xlsx';

export const MASS_ASSIGN_EXCEL_TEMPLATE = 'https://d2kh1tg8w32wdu.cloudfront.net/massive_assign.xlsx';

export const MASS_PROCESS_TYPE_EXCEL_TEMPLATE = 'https://cobranza-files-production.s3.us-east-1.amazonaws.com/massive_process_type.xlsx';

export const MISSING_DOCUMENTS_NOTIFICATION_EXCEL_TEMPLATE = 'https://cobranza-files-production.s3.us-east-1.amazonaws.com/Notificaci%C3%B3n+madiva+de+documentos+faltantes.xlsx';

export const MASS_LETTER_SENDING_EXCEL_TEMPLATE = 'https://d2kh1tg8w32wdu.cloudfront.net/envio_masivo_de_cartas.xlsx';

export const AUTHORIZATION_AGREEMENTS = [
  {
    title: 'Acuerdo de firma digital',
    controlName: 'acceptDigitalSignature',
    label: 'Acepto las condiciones de firma digital',
    errorMessage: 'Debe aceptar las condiciones de firma digital.'
  },
  {
    title: 'Estudio en centrales de riesgo',
    controlName: 'acceptRiskStudy',
    label: 'Acepto las condiciones de estudio en centrales de riesgo',
    errorMessage: 'Debe aceptar las condiciones de estudio en centrales de riesgo.'
  },
  {
    title: 'Tratamiento de datos personales',
    controlName: 'acceptDataTreatment',
    label: 'Acepto el tratamiento de mis datos personales',
    errorMessage: 'Debe aceptar el tratamiento de datos personales.'
  }
];
export const REMINDER_STATUS: Record<string, string> = {
  scheduled: 'Programado',
  sent: 'Enviado',
  default: ''
};

export const REMINDER_STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  sent: 'green',
  default: 'gray'
};

export const GENRES: Record<string, string> = {
  F: 'Femenino',
  M: 'Masculino',
};

export const REPORT_ACTIONS_DICC: Record<string, string> = {
  create_comment: 'Agregar comentario',
  assign_to: 'Asignar gestor',
  close_report: 'Cerrar reporte',
  end_coverage: 'Finalizar cobertura juridica',
  change_stage: 'Modificar etapa',
};

export const HISTORY_SECTIONS = {
  PUBLIC_SERVICES: 'publicServices',
  PENALTY_CLAUSES: 'penaltyClauses',
  DAMAGE_MISSING: 'damageMissing',
  REPORTS_DEBT: 'reportsDebt',
  DEFERRAL_EXCLUSION: 'deferralExclusion',
  TIMELINE: 'timeline',
  BREACH_REPORTS: 'breachReports',
  RESTRUCTURING_REPORT: 'restructuringReport',
  UPLOADED_DOCUMENTS: 'uploadedDocuments',
  TIMELINE_PORTFOLIO: 'timelinePortfolio'
} as const;

export const PAYMENT_SECTIONS = {
  PAYMENTS_TO_CLIENT: 'paymentsToClient',
  REFUND_REQUESTS: 'refundRequests',
} as const;

export type HistorySection =
  typeof HISTORY_SECTIONS[keyof typeof HISTORY_SECTIONS];

export type PaymentSection =
  typeof PAYMENT_SECTIONS[keyof typeof PAYMENT_SECTIONS];

export const SCORE_COLOR_MAP = {
  'A+': '#68de4a',
  A: '#22c55e',
  B: '#16a322',
  C: '#15803d',
  D: '#fde047',
  E: '#ca8a04',
  F: '#dc2626',
  R: '#7f1d1d',
} as const;

export type ScoreCategory = keyof typeof SCORE_COLOR_MAP;

export enum ScoreFormModeLabel {
  EDIT = 'Modificación',
  CREATE = 'Creación',
}

export const BREACH_REPORT_TRACEABILITY_TYPES: Record<string, string> = {
  breach_report_creation: 'Creación de reporte',
  breach_report_monthly_script_load: 'Cargue mensual de eventos',
  breach_report_update: 'Novedad en reporte',
  charge_creation_manual: 'Creación manual de cobro',
  imputation_upload: 'Carga de imputación',
  imputation_creation_manual: 'Creación manual de imputación',
  imputation_creation_automatic: 'Creación automática de imputación',
  imputation_update: 'Actualización de imputación',
  imputation_deletion: 'Eliminación de imputación',
};

export const BREACH_REPORT_TRACEABILITY_ORIGIN: Record<string, string> = {
  system: 'Sistema',
  administrator: 'Administrador',
  user: 'Gestor',
};

export const BREACH_REPORT_TRACEABILITY_ORIGIN_COLORS: Record<string, string> = {
  system: '#546E7A',
  administrator: '#1E88E5',
  user: '#43A047',
};

export const BREACH_REPORT_TRACEABILITY_COLORS: Record<string, string> = {
  default: '#9E9E9E',
  breach_report_creation: '#1E88E5',
  breach_report_monthly_script_load: '#00ACC1',
  breach_report_update: '#8E24AA',
  charge_creation_manual: '#43A047',
  imputation_upload: '#FB8C00',
  imputation_creation_manual: '#558B2F',
  imputation_creation_automatic: '#2E7D32',
  imputation_update: '#F9A825',
  imputation_deletion: '#E53935',
};

export const BREACH_REPORT_TRACEABILITY_ICONS: Record<string, string> = {
  default: '<i class="fa-solid fa-clock"></i>',
  breach_report_creation: '<i class="fa-solid fa-file-circle-plus"></i>',
  breach_report_monthly_script_load: '<i class="fa-solid fa-cloud-arrow-down"></i>',
  breach_report_update: '<i class="fa-solid fa-pen-to-square"></i>',
  charge_creation_manual: '<i class="fa-solid fa-money-bill-wave"></i>',
  imputation_upload: '<i class="fa-solid fa-file-arrow-up"></i>',
  imputation_creation_manual: '<i class="fa-solid fa-list-check"></i>',
  imputation_creation_automatic: '<i class="fa-solid fa-gears"></i>',
  imputation_update: '<i class="fa-solid fa-pen"></i>',
  imputation_deletion: '<i class="fa-solid fa-trash"></i>',
};

export const BREACH_REPORT_TRACEABILITY_STEP_VALUES: Record<string, string> = {
  upload_imputation_information: 'Cargar información de imputación',
  simulate_imputation: 'Simular imputación',
  confirm_imputation: 'Confirmar imputación',
  save_imputation: 'Guardar imputación',
  clean_form: 'Limpiar formulario',
  charge_creation: 'Creación de cobro',
};


export const BREACH_REPORT_TRACEABILITY_DATA_VALUES: Record<string, string> = {
  administrator: 'Administrador',
  user: 'Gestor',
  system: 'Sistema',
};

export const BREACH_REPORT_TRACEABILITY_DATA_KEYS: Record<string, string> = {
  amount: 'Monto',
  reference: 'Referencia',
  description: 'Descripción',
  payment_date: 'Fecha de pago',
  event_id: 'ID de evento',
  imputation_id: 'ID de imputación',
  status: 'Estado',
  notes: 'Notas',
  step: 'Etapa',
  created_by: 'Usuario creador (ID)',
  payment_type: 'Tipo de pago',
  applied_amount: 'Monto aplicado',
  third_payment: 'Abono a terceros',
  adjustment_applied_amount: 'Ajuste al peso',
  type_charge: 'Tipo de cobro',
  type: 'Tipo',
};

export const BREACH_REPORT_TRACEABILITY_TYPE_CHARGE_LABELS: Record<string, string> = {
  ...OPTIONS_COLLECTIONS,
  ...ASPIRATIONS_MAP,
};

export const BREACH_REPORT_TRACEABILITY_AMOUNT_KEYS: readonly string[] = [
  'amount',
  'applied_amount',
  'third_payment',
  'adjustment_applied_amount',
  'monto',
];

export const BREACH_REPORT_TRACEABILITY_VALUE_MAPS_BY_FIELD: Record<
  string,
  Record<string, string>
> = {
  payment_type: MOVEMENTS_AMOUNT_TYPE,
  type: BREACH_REPORT_TRACEABILITY_TYPES,
  step: BREACH_REPORT_TRACEABILITY_STEP_VALUES,
  type_charge: BREACH_REPORT_TRACEABILITY_TYPE_CHARGE_LABELS,
};

export enum ReportRiskLabel {
  NO_RISK = 'Sin riesgo',
  RISK = 'Con riesgo'
}
